import Axios from 'axios';
import { FILTER_API_HOST } from 'Config/constants';
import { Course } from 'services/courses';
import { Event } from 'services/events';
import { Filter } from 'services/filtering';
import { DateAndTime } from 'services/interface';
import { TSortOption } from 'services/publisher/interface';
import { LibraryItem } from 'services/singles';

export interface Epoch {
  epoch: number;
}

export type UserRegion = {
  name?: string;
  location?: {
    lat: number;
    lon: number;
  };
};

export interface User {
  accept_donations?: boolean;
  has_avatar?: boolean;
  id: string;
  is_publisher?: boolean;
  is_therapist?: boolean;
  is_verified_therapist?: boolean;
  name: string;
  first_name?: string;
  region?: UserRegion;
  username?: string;
  tagline?: string;
  created_at?: Epoch;
  follower_count?: number;
  publisher_follower_count?: number;
  publisher_play_count?: number;
  publisher_description?: string;
  publisher_enterprise_description?: string;
  total_minutes?: number;
  description?: string;
  is_yoga_teacher?: boolean;
  publisher_languages?: string[];
  publisher_approval_date?: DateAndTime;
  therapist_approval_date?: DateAndTime;
  website?: string;
  blurhash?: string;
  can_host_livestream?: boolean;
  is_deleted?: boolean;
  total_courses?: number;
  total_free_tracks?: number;
  updated_at?: Epoch;
  number_of_followers?: number;
}

export interface CollectionResponse {
  courses: Course[];
  libraryitems: LibraryItem[];
}

const API_HOST = process.env.REACT_APP_FILTER_API_HOST;
const SITEMAP_HOST = process.env.REACT_APP_SITEMAP_HOST;

export const getUserDetails = async (userId: string) => {
  const response = await Axios.get<User>(
    `${process.env.REACT_APP_USER_HOST}/${userId}/data/user.json`,
    {
      timeout: 10000
    }
  );

  return response?.data;
};

interface GetTeacherItemResponse {
  item_summary: {
    user_summary: User;
  };
}

export const getTeacher = async (slug: string): Promise<User | null> => {
  let userId: string;
  const response = await Axios.get<GetTeacherItemResponse[]>(
    `${API_HOST}/api/v1/publishers/filter?username=${slug}`,
    {
      timeout: 10000
    }
  );

  const { data } = response ?? {};
  const userSummary = data.find(
    ({ item_summary }) =>
      item_summary?.user_summary?.username?.toLowerCase() === slug.toLowerCase()
  )?.item_summary?.user_summary;

  if (!userSummary) {
    // Fallback fetch to therapist
    const therapist = await Axios.get<GetTeacherItemResponse[]>(
      `${API_HOST}/api/v1/therapists/filter?username=${slug}`,
      {
        timeout: 10000
      }
    );

    const therapistData = therapist?.data?.find(
      ({ item_summary }) =>
        item_summary?.user_summary?.username?.toLowerCase() ===
        slug.toLowerCase()
    )?.item_summary?.user_summary;

    if (therapistData) {
      userId = therapistData?.id;
    } else {
      return null; // No user found with the given slug
    }
  } else {
    userId = userSummary?.id;
  }

  const userDetails = await getUserDetails(userId);

  return userDetails;
};

export const getTeacherCollection = async (
  teacherID: string
): Promise<CollectionResponse> => {
  const response = await Axios.get(
    `${SITEMAP_HOST}/teachers/${teacherID}.json`
  );
  const { data } = response;
  return data;
};

export interface PublisherProfile {
  upcoming_events?: Event[];
}

interface EventItem {
  item_summary: {
    event_summary: Event;
  };
}

interface GetPublisherEventsParams {
  publisherId: string;
  filter: Filter;
}

export const getPublisherEvents = async ({
  publisherId,
  filter
}: GetPublisherEventsParams) => {
  const response = await Axios.get<EventItem[]>(
    `${FILTER_API_HOST}/api/v1/publishers/${publisherId}/live-events`,
    {
      params: filter
    }
  );
  const publisherLiveEventList =
    response.data?.map(event => event.item_summary.event_summary) ?? [];

  return publisherLiveEventList;
};

interface RelatedTeacherItem {
  item_summary: {
    user_summary: User;
  };
}

interface GetRelatedTeacherParams {
  publisherId: string;
  limit: number;
  sort_option: TSortOption;
}

export const getRelatedTeacher = async ({
  publisherId,
  limit,
  sort_option
}: GetRelatedTeacherParams) => {
  const response = await Axios.get<RelatedTeacherItem[]>(
    `${FILTER_API_HOST}/api/v1/publishers/${publisherId}/related`,
    {
      params: {
        limit,
        sort_option
      }
    }
  );

  return response.data;
};
